import { useEffect, useState } from "react";
import { getMyResult, type MemberResult } from "../../services/me-api";

const STATUS_LABEL: Record<MemberResult["status"], string> = {
  not_started: "Not started",
  in_progress: "In progress",
  submitted: "Submitted",
  pending_grading: "Pending grading",
  completed: "Completed",
};

/** FR-019/FR-029/FR-030: the member's own finalized result, or a pending-grading notice while
 * free-text answers await a CO Member's grading. Explanations only render once released. */
export function ResultPage() {
  const [result, setResult] = useState<MemberResult | null | "loading">("loading");

  useEffect(() => {
    getMyResult().then(setResult);
  }, []);

  if (result === "loading") return <div className="p-6 text-body">Loading…</div>;
  if (!result) return <div className="p-6 text-body">You haven't submitted a test yet.</div>;

  if (result.status === "pending_grading") {
    return (
      <div className="p-6">
        <h1 className="mb-2 font-sans text-xl text-ink">Result Pending</h1>
        <p className="text-body">
          Your test has been submitted. It includes one or more free-text answers awaiting review
          by a CO Member — your score will appear here once grading is complete.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <h1 className="mb-2 font-sans text-xl text-ink">Your Result</h1>
      <p className="mb-6 text-body">
        Status: {STATUS_LABEL[result.status]}
        {result.scorePercent !== null && ` · Score: ${result.scorePercent}%`}
        {result.passed !== null && ` · ${result.passed ? "Passed" : "Not passed"}`}
      </p>

      <div className="flex flex-col gap-6">
        {result.questions.map((q) => (
          <div key={q.questionId} className="rounded border border-surface-alt p-4">
            <p className="mb-2 font-sans text-ink">{q.text}</p>
            <p className="mb-1 text-sm text-body">
              Your answer: {Array.isArray(q.given) ? q.given.join(", ") : (q.given ?? "—")}
              {q.isCorrect !== null && (
                <span className={q.isCorrect ? "ml-2 text-brand" : "ml-2 text-accent"}>
                  {q.isCorrect ? "Correct" : "Incorrect"}
                </span>
              )}
            </p>
            {result.explanationsReleased && q.correctAnswer && (
              <p className="mb-1 text-sm text-body">Correct answer: {q.correctAnswer.join(", ")}</p>
            )}
            {result.explanationsReleased && q.explanationHtml && (
              <div
                className="rich-text prose prose-sm mt-2 max-w-none text-body"
                dangerouslySetInnerHTML={{ __html: q.explanationHtml }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
